import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
  GetCommunityPostsDocument,
  GetCommunityPostsQuery,
  GetCommunityPostsQueryVariables,
  useGetCommunityPostsQuery,
} from 'generated/graphql';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { Post, PostId } from '../model/post.types';
import { toPost } from '../model/post.mappers';
import { CommunityId } from '../model/community.types';

const FEED_POLL_INTERVAL_MS = 30_000;
const OLDER_PAGE_DIRECTION = 'olderThan';

export type CommunityFeedResult = {
  posts: Async<Post[]>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

const toPosts = (data: GetCommunityPostsQuery): Post[] =>
  data.communityPosts ? data.communityPosts.posts.map(toPost) : [];

const dedupeById = (posts: Post[]): Post[] => {
  const seen = new Set<PostId>();
  const result: Post[] = [];
  for (const post of posts) {
    if (seen.has(post.id)) continue;
    seen.add(post.id);
    result.push(post);
  }
  return result;
};

export const useCommunityFeed = (
  communityId: CommunityId,
  canModerate: boolean,
): CommunityFeedResult => {
  const client = useApolloClient();
  const query = useGetCommunityPostsQuery({
    variables: { id: communityId, canModerate },
    fetchPolicy: 'cache-and-network',
    pollInterval: FEED_POLL_INTERVAL_MS,
  });

  const { data: firstPage } = useAsyncQueryRaw({ query, mapper: toPosts });

  // Older pages live outside the normalized cache; they reconcile on the next load.
  const [olderPosts, setOlderPosts] = useState<Post[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    setOlderPosts([]);
    setIsLoadingMore(false);
    setReachedEnd(false);
  }, [communityId, canModerate]);

  const posts: Async<Post[]> =
    firstPage.type === 'fetched'
      ? toAsyncFetched(dedupeById([...firstPage.value, ...olderPosts]))
      : firstPage;

  const hasMore =
    posts.type === 'fetched' && posts.value.length > 0 && !reachedEnd;

  const loadMore = useCallback(() => {
    if (isLoadingMore || reachedEnd) return;
    if (posts.type !== 'fetched' || posts.value.length === 0) return;
    const lastId = posts.value[posts.value.length - 1].id;

    setIsLoadingMore(true);
    client
      .query<GetCommunityPostsQuery, GetCommunityPostsQueryVariables>({
        query: GetCommunityPostsDocument,
        variables: {
          id: communityId,
          canModerate,
          direction: OLDER_PAGE_DIRECTION,
          lastId,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        const page = toPosts(data);
        if (page.length === 0) {
          setReachedEnd(true);
          return;
        }
        setOlderPosts((prev) => dedupeById([...prev, ...page]));
      })
      .catch(() => setReachedEnd(true))
      .finally(() => setIsLoadingMore(false));
  }, [client, communityId, canModerate, isLoadingMore, reachedEnd, posts]);

  return { posts, hasMore, isLoadingMore, loadMore };
};
